import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { Activity, MessageCircle, CheckCircle2, AlertCircle, Plus, Edit2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const safeDistance = (iso) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ''; }
};

const iconFor = (action) => {
  if (action.includes('approval')) return { Icon: CheckCircle2, color: '#00C972' };
  if (action.includes('revision')) return { Icon: AlertCircle, color: '#FFB800' };
  if (action.includes('comment')) return { Icon: MessageCircle, color: '#0055FF' };
  if (action.includes('created')) return { Icon: Plus, color: '#FF451A' };
  if (action.includes('state_changed') || action.includes('updated')) return { Icon: Edit2, color: '#9B6EFF' };
  return { Icon: Activity, color: '#666666' };
};

export const ActivityFeed = ({ campaignId, limit = 30, title = 'Activity', emptyText = 'No activity yet.' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeed = async () => {
    try {
      const url = campaignId
        ? `${API_URL}/api/activity?campaign_id=${campaignId}&limit=${limit}`
        : `${API_URL}/api/activity?limit=${limit}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setItems(data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeed(); /* eslint-disable-next-line */ }, [campaignId]);

  return (
    <div className="bg-transparent" data-testid="activity-feed">
      {title && (
        <div className="flex items-center justify-between mb-4 px-5 pt-5">
          <h3 className="font-semibold text-white flex items-center gap-2"><Activity size={16} /> {title}</h3>
          <button onClick={fetchFeed} className="text-xs text-[#FF451A] hover:text-white font-medium">Refresh</button>
        </div>
      )}
      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-500">{emptyText}</div>
      ) : (
        <div className="space-y-2 p-5">
          {items.map((it) => {
            const { Icon, color } = iconFor(it.action);
            return (
              <div key={it.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/[0.02] transition-colors" data-testid="activity-item">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20`, border: `1px solid ${color}30` }}>
                  <Icon size={14} style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white leading-snug">
                    <span className="font-medium">{it.user_name}</span>{' '}
                    <span className="text-zinc-400">{it.description}</span>
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 mt-1">{safeDistance(it.created_at)} · {it.user_role?.replace('_', ' ')}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
